import React, { useEffect, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AuthProvider } from "react-oidc-context";
import { ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";

import { createQueryClient } from "./config/react-query";
import { createAppUserManager } from "./services/auth";
import store from "./store";
import bootstrap from "./utils/bootstrap";
import { IAppData } from "./utils/bootstrap/types";
import Main from "./pages/Main";
import { AbilityProvider } from "./components/Can";
import { updateMetadata } from "./store/metadata";
import { BRAND_MAP } from "./utils/tenant/constants";
import i18n from "./i18n";
import WrappedRecurlyProvider from "./components/WrappedRecurlyProvider";
import { getRecurlyKey, isSubscriptionPage } from "./utils/common";
import { Loader } from "./components/Loader/Loader";

import "./styles.scss";

export default function App() {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

function AppContent() {
  const { status, data } = useQuery<IAppData, Error>({
    queryKey: ["appData"],
    queryFn: bootstrap,
    retry: 3,
  });
  const brandCode = data?.originInfo.tenant_code || data?.originInfo.kind;
  const brandCodeName = data?.originInfo.tenant_name || data?.originInfo.kind;
  const recurlyPublicKey = getRecurlyKey(data?.originInfo?.sps);

  const userManager = useMemo(() => {
    return status === "success"
      ? createAppUserManager(data?.originInfo?.oidc)
      : undefined;
  }, [status, data]);

  useEffect(() => {
    if (status === "success") {
      if (brandCode) {
        store.dispatch(updateMetadata({ brandCode, brandCodeName }));

        if (brandCode === BRAND_MAP.ADMIN.code) {
          i18n.changeLanguage("en");
        }
      }
    }
  }, [brandCode, brandCodeName, status]);

  if (status !== "success" || !userManager) {
    return (
      <Loader className="App" isSubscriptionLoader={isSubscriptionPage()} />
    );
  }

  return (
    <Sentry.ErrorBoundary>
      <Provider store={store}>
        {/* The signin callback is handled by the OidcSigninCallback page. */}
        <AuthProvider userManager={userManager} skipSigninCallback>
          <WrappedRecurlyProvider publicKey={recurlyPublicKey}>
            <AbilityProvider>
              <Router>
                <Main />
                <ToastContainer />
              </Router>
            </AbilityProvider>
          </WrappedRecurlyProvider>
        </AuthProvider>
      </Provider>
    </Sentry.ErrorBoundary>
  );
}
